// src/hooks/useComprasMutations.ts
import { apiPost, apiPut } from '../services/api';
import { resolveLoteError } from '../services/loteError';
import { Compra, OrdemCompra, PreOrdem } from '../types/compras/Compra';
import { useApiLock } from './useApiLock';
import { useDragAndDrop } from './useDragAndDrop';
import { showAlert } from '../contexts/DialogContext';
import { Toast } from '../contexts/ToastContext';
import { logger } from '@/utils/logger';

type SourceColumn = 'pending' | 'cotacoes' | 'aberta' | 'finalizada';
type DragData = Compra | PreOrdem | OrdemCompra;

interface ColumnApi<T> {
  updateItems: (updater: (prev: T[]) => T[]) => void;
  reset: () => void;
}

interface Options {
  compras: ColumnApi<Compra>;
  preOrdens: ColumnApi<PreOrdem>;
  ordensAbertas: ColumnApi<OrdemCompra>;
  ordensFinalizadas: ColumnApi<OrdemCompra>;
}

export const useComprasMutations = ({
  compras,
  preOrdens,
  ordensAbertas,
  ordensFinalizadas: _ordensFinalizadas,
}: Options) => {
  const { isLocked, lock, unlock } = useApiLock();

  const drag = useDragAndDrop<DragData, SourceColumn>({
    onDropHandler: async (data, source, target) => {
      if (source === target) return;

      try {
        if (source === 'pending' && target === 'cotacoes') {
          const draggedCompra = data as Compra;
          const key = `/v1/pre-ordens_${draggedCompra.id}`;
          if (isLocked(key)) return;
          lock(key);
          try {
            const payload = {
              itens: draggedCompra.compraItems.map((item) => ({
                requisicaoItemId: item.id,
                qtdProduto: item.qtdSaldoRequisicao,
              })),
            };
            const response = await apiPost('/v1/pre-ordens', payload);
            const novaPreOrdem: PreOrdem = response.data;
            preOrdens.updateItems((prev) => [...prev, novaPreOrdem]);
            compras.updateItems((prev) => prev.filter((c) => c.id !== draggedCompra.id));
            compras.reset();
            preOrdens.reset();
            const successMessage = response.data.message || 'Pré-ordem criada com sucesso';
            Toast.success(successMessage);
          } finally {
            unlock(key);
          }
        } else if (source === 'cotacoes' && target === 'aberta') {
          const draggedPreOrdem = data as PreOrdem;
          const key = `/v1/ordens-compra_${draggedPreOrdem.id}`;
          if (isLocked(key)) return;
          lock(key);
          try {
            const payload = {
              preOrdemId: draggedPreOrdem.id,
              itens: draggedPreOrdem.itens.map((item) => ({
                requisicaoItemId: item.id,
                qtdCompra: item.qtdProdutoOriginal,
              })),
            };
            const response = await apiPost('/v1/ordens-compra', payload);
            const novaOrdem: OrdemCompra = response.data;
            ordensAbertas.updateItems((prev) => [...prev, novaOrdem]);
            preOrdens.updateItems((prev) => prev.filter((p) => p.id !== draggedPreOrdem.id));
            preOrdens.reset();
            ordensAbertas.reset();
            const successMessage = response.data.message || 'Ordem de compra criada com sucesso';
            Toast.success(successMessage);
          } finally {
            unlock(key);
          }
        } else if (source === 'cotacoes' && target === 'pending') {
          const draggedPreOrdem = data as PreOrdem;
          const key = `/v1/pre-ordens/${draggedPreOrdem.id}/cancelar`;
          if (isLocked(key)) return;
          lock(key);
          try {
            const response = await apiPut(`/v1/pre-ordens/${draggedPreOrdem.id}/cancelar`, {});
            preOrdens.updateItems((prev) => prev.filter((p) => p.id !== draggedPreOrdem.id));
            preOrdens.reset();
            compras.reset();
            const successMessage = response.data.message || 'Pré-ordem cancelada com sucesso';
            Toast.success(successMessage);
          } finally {
            unlock(key);
          }
        } else if (source === 'aberta' && target === 'cotacoes') {
          const draggedOrdem = data as OrdemCompra;
          const key = `/v1/ordens-compra/${draggedOrdem.id}/cancelar`;
          if (isLocked(key)) return;
          lock(key);
          try {
            const response = await apiPut(`/v1/ordens-compra/${draggedOrdem.id}/cancelar`, {});
            ordensAbertas.updateItems((prev) => prev.filter((o) => o.id !== draggedOrdem.id));
            ordensAbertas.reset();
            preOrdens.reset();
            const successMessage = response.data.message || 'Ordem de compra cancelada com sucesso';
            Toast.success(successMessage);
          } finally {
            unlock(key);
          }
        }
      } catch (err) {
        logger.error('Erro ao processar operação:', err);
        // A recusa da trava de lote sai como aviso de espera, não como erro: o
        // cancelamento de cotação é recusado enquanto há remoção em lote em voo
        // na mesma pré-ordem, e o usuário só precisa repetir depois (ADR-048 do
        // backend). Os demais erros seguem exatamente como antes.
        const alerta = resolveLoteError(err, 'Erro ao processar a operação. Tente novamente.');
        await showAlert(alerta.message, { variant: alerta.variant });
      }
    },
  });

  const onDragStartPending = (data: Compra) => drag.onDragStart(data, 'pending');
  const onDragStartCotacoes = (data: PreOrdem) => drag.onDragStart(data, 'cotacoes');
  const onDragStartAberta = (data: OrdemCompra) => drag.onDragStart(data, 'aberta');
  const onDragStartFinalizada = (data: OrdemCompra) => drag.onDragStart(data, 'finalizada');

  return {
    onDragStartPending,
    onDragStartCotacoes,
    onDragStartAberta,
    onDragStartFinalizada,
    onDragOver: drag.onDragOver,
    onDrop: drag.onDrop,
  };
};
